import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export type WakeLockState = "locked" | "requested" | "released" | "unsupported";

// Define the shape of the context data
interface TabManagementContextType {
  isTabActive: boolean;
  wakeLockState: WakeLockState;
  setRequestWakeLock: Dispatch<SetStateAction<boolean>>;
}

const TabManagementContext = createContext<
  TabManagementContextType | undefined
>(undefined);

export const TabManagementContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [isTabActive, setIsTabActive] = useState(true);
  const [wakeLockState, setWakeLockState] = useState<WakeLockState>("released");
  const [requestWakeLock, setRequestWakeLock] = useState(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (requestWakeLock) {
      requestLock();
    } else {
      releaseLock();
    }
  }, [requestWakeLock]);

  const requestLock = useCallback(async () => {
    if (wakeLockRef.current && !requestWakeLock) {
      return;
    }

    if (!("wakeLock" in navigator)) {
      setWakeLockState("unsupported");
      return;
    }

    try {
      setWakeLockState("requested");
      wakeLockRef.current = await navigator.wakeLock.request("screen");

      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
        setWakeLockState("released");
      });

      setWakeLockState("locked");
    } catch (err: any) {
      console.error(`Failed to get wake lock: ${err.name}, ${err.message}`);
      setWakeLockState("unsupported");
    }
  }, []);

  const releaseLock = useCallback(async () => {
    if (!wakeLockRef.current) {
      return;
    }
    try {
      console.log("Releasing wake lock...");
      await wakeLockRef.current.release();
    } catch (err: any) {
      console.error(`Failed to release wake lock: ${err.name}, ${err.message}`);
    }
  }, []);

  const handleVisibilityChange = useCallback(() => {
    const isVisible = document.visibilityState === "visible";
    setIsTabActive(isVisible);

    if (isVisible) {
      requestLock();
    } else {
      // Don't release on visibility change, browser does it automatically.
      // The 'release' event listener will fire.
    }
  }, [requestLock]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      releaseLock();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange, requestLock, releaseLock]);

  return (
    <TabManagementContext.Provider
      value={{
        isTabActive,
        wakeLockState,
        setRequestWakeLock,
      }}
    >
      {children}
    </TabManagementContext.Provider>
  );
};

// Create the custom hook to consume the context
export const useTabManagement = () => {
  const context = useContext(TabManagementContext);
  if (context === undefined) {
    throw new Error(
      "useTabActive must be used within a TabManagementContextProvider",
    );
  }
  return context;
};
